import React, { type FC, type ReactNode, useEffect, useRef } from 'react';

import { Button } from '@shared/ui/button';
import IconError from '@assets/img/icons/alert-octagon.svg';
import IconWarning from '@assets/img/icons/alert-triangle.svg';
import IconSuccess from '@assets/img/icons/check-circle-2.svg';
import IconClose from '@assets/img/icons/close-x.svg';
import IconInfo from '@assets/img/icons/info.svg';

import type { MessageProps } from '../model/types';

import styles from './styles.module.scss';

export const Message: FC<MessageProps> = ({
	id,
	onRemove,
	message,
	hasRemove = false,
	type = 'custom',
	duration = 4000,
	className = '',
}) => {
	const refText = useRef<HTMLDivElement>(null);
	const refMessage = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const messageEl = refMessage.current;

		const onAnimationend = () => onRemove(id);

		const startTimeout = () => {
			if (!messageEl || duration === 0) return;

			timeoutRef.current = setTimeout(() => {
				messageEl.classList.remove(styles.slideInRight);
				messageEl.classList.add(styles.bounceOutRight);
				messageEl.addEventListener('animationend', onAnimationend);
			}, duration);
		};

		const cancelTimeout = () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			if (messageEl) {
				messageEl.classList.remove(styles.bounceOutRight);
				messageEl.removeEventListener('animationend', onAnimationend);
			}
		};

		startTimeout();

		if (messageEl && duration !== 0) {
			messageEl.addEventListener('mouseenter', cancelTimeout);
			messageEl.addEventListener('mouseleave', startTimeout);
		}

		return () => {
			cancelTimeout();
			if (messageEl) {
				messageEl.removeEventListener('mouseenter', cancelTimeout);
				messageEl.removeEventListener('mouseleave', startTimeout);
			}
		};
	}, [id, duration, onRemove]);

	let icon: ReactNode = null;
	switch (type) {
		case 'info': {
			icon = IconInfo;
			break;
		}
		case 'error': {
			icon = IconError;
			break;
		}
		case 'success': {
			icon = IconSuccess;
			break;
		}
		case 'warning': {
			icon = IconWarning;
			break;
		}
		default: {
			break;
		}
	}

	return (
		<div className={`${styles.messageContainer} ${className}`}>
			<div ref={refMessage} className={`${styles.message} ${styles.slideInRight}`}>
				<div className={styles.messageContent}>
					{icon && (
						<div
							className={`${styles.messageIcon} ${styles[type as keyof typeof styles]}`}
							dangerouslySetInnerHTML={{ __html: icon }}
						/>
					)}
					<div ref={refText} className={`${styles.messageText}`}>
						{message}
					</div>
					{hasRemove && (
						<div className={`${styles.messageControls}`}>
							<Button buttonType="icon-mini" svg={IconClose} onClick={() => onRemove(id)} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
